import type {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactElement,
  ReactNode
} from 'react'
import { cloneElement, isValidElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/cn'

export interface DropdownMenuAction {
  label: string
  onSelect: () => void
  icon?: ReactNode
  danger?: boolean
  disabled?: boolean
}

export interface DropdownMenuSeparator {
  separator: true
}

export type DropdownMenuItem = DropdownMenuAction | DropdownMenuSeparator

export interface DropdownMenuProps {
  trigger: ReactNode
  items: Array<DropdownMenuItem>
  align?: 'start' | 'end'
}

interface TriggerHandlers {
  onClick?: (event: ReactMouseEvent<HTMLElement>) => void
  onKeyDown?: (event: ReactKeyboardEvent<HTMLElement>) => void
}

function isSeparator(item: DropdownMenuItem): item is DropdownMenuSeparator {
  return 'separator' in item
}

const TRIGGER_ATTR = 'data-argus-menu-trigger'

export function DropdownMenu({ trigger, items, align = 'end' }: DropdownMenuProps): ReactElement {
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const rootRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([])

  const selectableIndexes = useMemo(
    () =>
      items.reduce<number[]>((acc, item, index) => {
        if (!isSeparator(item) && item.disabled !== true) acc.push(index)
        return acc
      }, []),
    [items]
  )

  // The trigger is cloned rather than ref'd, so a caller's own ref survives.
  const focusTrigger = useCallback(() => {
    rootRef.current?.querySelector<HTMLElement>(`[${TRIGGER_ATTR}]`)?.focus()
  }, [])

  const close = useCallback(
    (restoreFocus: boolean) => {
      setOpen(false)
      setActiveIndex(-1)
      if (restoreFocus) focusTrigger()
    },
    [focusTrigger]
  )

  const openAt = (position: 'first' | 'last'): void => {
    const index =
      position === 'first' ? selectableIndexes[0] : selectableIndexes[selectableIndexes.length - 1]
    setActiveIndex(index ?? -1)
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent): void => {
      if (rootRef.current?.contains(event.target as Node) === true) return
      setOpen(false)
      setActiveIndex(-1)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  useEffect(() => {
    if (!open || activeIndex < 0) return
    itemRefs.current[activeIndex]?.focus()
  }, [open, activeIndex])

  const step = (delta: number): void => {
    if (selectableIndexes.length === 0) return
    const current = selectableIndexes.indexOf(activeIndex)
    const nextPosition =
      current === -1
        ? delta > 0
          ? 0
          : selectableIndexes.length - 1
        : (current + delta + selectableIndexes.length) % selectableIndexes.length
    const next = selectableIndexes[nextPosition]
    if (next !== undefined) setActiveIndex(next)
  }

  const onMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    switch (event.key) {
      case 'Escape':
        event.preventDefault()
        event.stopPropagation() // never let Escape also close a surrounding dialog
        close(true)
        break
      case 'Tab':
        close(false)
        break
      case 'ArrowDown':
        event.preventDefault()
        step(1)
        break
      case 'ArrowUp':
        event.preventDefault()
        step(-1)
        break
      case 'Home':
        event.preventDefault()
        setActiveIndex(selectableIndexes[0] ?? -1)
        break
      case 'End':
        event.preventDefault()
        setActiveIndex(selectableIndexes[selectableIndexes.length - 1] ?? -1)
        break
      default:
        break
    }
  }

  const handlers = isValidElement(trigger) ? (trigger.props as TriggerHandlers) : null

  const triggerNode = isValidElement(trigger) ? (
    cloneElement(trigger as ReactElement<Record<string, unknown>>, {
      [TRIGGER_ATTR]: '',
      'aria-haspopup': 'menu',
      'aria-expanded': open,
      onClick: (event: ReactMouseEvent<HTMLElement>) => {
        handlers?.onClick?.(event)
        if (event.defaultPrevented) return
        if (open) close(false)
        else openAt('first')
      },
      onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => {
        handlers?.onKeyDown?.(event)
        if (event.defaultPrevented) return
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          openAt('first')
        } else if (event.key === 'ArrowUp') {
          event.preventDefault()
          openAt('last')
        }
      }
    })
  ) : (
    <button
      type="button"
      {...{ [TRIGGER_ATTR]: '' }}
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={() => (open ? close(false) : openAt('first'))}
      className="inline-flex items-center rounded-control px-2 py-1 text-sm text-fg"
    >
      {trigger}
    </button>
  )

  return (
    <div ref={rootRef} className="relative inline-flex">
      {triggerNode}
      {open ? (
        <div
          role="menu"
          aria-orientation="vertical"
          onKeyDown={onMenuKeyDown}
          className={cn(
            'animate-rise absolute top-full z-50 mt-2 min-w-[12rem] max-w-[min(20rem,calc(100vw-2rem))] rounded-card border border-line bg-raised p-1 shadow-pop',
            align === 'end' ? 'right-0 origin-top-right' : 'left-0 origin-top-left'
          )}
        >
          {items.map((item, index) => {
            if (isSeparator(item)) {
              return <div key={`separator-${index}`} role="separator" className="my-1 h-px bg-line" />
            }
            const disabled = item.disabled === true
            return (
              <button
                key={item.label}
                ref={(node) => {
                  itemRefs.current[index] = node
                }}
                type="button"
                role="menuitem"
                tabIndex={index === activeIndex ? 0 : -1}
                disabled={disabled}
                onMouseEnter={() => {
                  if (!disabled) setActiveIndex(index)
                }}
                onClick={() => {
                  close(true)
                  item.onSelect()
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-[0.5rem] px-2.5 py-2 text-left text-sm font-medium transition-colors [&_svg]:size-4 [&_svg]:shrink-0',
                  disabled && 'cursor-not-allowed opacity-50',
                  item.danger === true ? 'text-bad-600 dark:text-bad-300' : 'text-fg',
                  !disabled &&
                    (item.danger === true
                      ? 'hover:bg-bad-100 dark:hover:bg-bad-950'
                      : 'hover:bg-sunken')
                )}
              >
                {item.icon ? (
                  <span className="inline-flex shrink-0 opacity-70" aria-hidden="true">
                    {item.icon}
                  </span>
                ) : null}
                <span className="truncate">{item.label}</span>
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
